import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { printReceipt, downloadReceiptPDF, printReturn, downloadReturnPDF } from "@/lib/receipt";
import { InvoiceModal } from "@/pages/POS";
import { Printer, Download, RotateCcw, X, Plus, Trash2, AlertTriangle, CheckCircle2, FileSpreadsheet } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const PAY = ["Cash", "Debit", "Credit Card", "E-Transfer", "Other"];
const PERIODS = [["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["custom", "Custom"]];

export const POSDashboardTab = () => {
  const [period, setPeriod] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [d, setD] = useState(null);
  const load = () => api.get("/pos/dashboard", { params: { period, date_from: from || undefined, date_to: to || undefined } }).then((r) => setD(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, from, to]);
  if (!d) return <div className="text-slate-400">Loading…</div>;
  const Stat = ({ label, value, testid }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs uppercase tracking-wider text-slate-500">{label}</div><div className="font-heading text-2xl text-brand-navy mt-1" data-testid={testid}>{value}</div></div>
  );
  return (
    <div className="space-y-4" data-testid="pos-dashboard-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading text-2xl text-brand-navy">Prepare Order Dashboard</div>
        <div className="flex gap-1 flex-wrap">
          {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} data-testid={`pos-period-${k}`} className={`px-3 h-9 rounded-full text-sm font-bold ${period === k ? "bg-brand-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>)}
        </div>
      </div>
      {period === "custom" && <div className="flex gap-2"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 border rounded-lg px-3" /><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 border rounded-lg px-3" /></div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Gross sales" value={money(d.gross_sales)} testid="pos-stat-gross" />
        <Stat label="Transactions" value={d.transactions} testid="pos-stat-tx" />
        <Stat label="Avg transaction" value={money(d.average_transaction)} />
        <Stat label="Net sales" value={money(d.net_sales)} testid="pos-stat-net" />
        <Stat label="Cash" value={money(d.by_method.Cash)} />
        <Stat label="Debit" value={money(d.by_method.Debit)} />
        <Stat label="Credit Card" value={money(d.by_method["Credit Card"])} />
        <Stat label="E-Transfer" value={money(d.by_method["E-Transfer"])} />
        <Stat label="Returns" value={money(d.returns)} />
        <Stat label="Discounts" value={money(d.discounts)} />
        <Stat label="HST collected" value={money(d.hst)} />
        <Stat label="Return count" value={d.return_count} />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <Panel title="Top sellers">{d.top_products.length ? d.top_products.map((p, i) => <li key={i} className="flex justify-between py-1 text-sm"><span className="truncate">{p.product_name}</span><span className="font-bold">{p.qty}× · {money(p.revenue)}</span></li>) : <li className="text-slate-400 text-sm">No sales yet.</li>}</Panel>
        <Panel title="Low stock">{d.low_stock.length ? d.low_stock.map((p, i) => <li key={i} className="flex justify-between py-1 text-sm"><span className="truncate">{p.product_name}</span><span className="font-bold text-amber-600">{p.available}</span></li>) : <li className="text-slate-400 text-sm">None.</li>}</Panel>
        <Panel title="Out of stock">{d.out_of_stock.length ? d.out_of_stock.map((p, i) => <li key={i} className="py-1 text-sm truncate">{p.product_name}</li>) : <li className="text-slate-400 text-sm">None.</li>}</Panel>
      </div>
    </div>
  );
};
const Panel = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="font-bold text-brand-navy mb-2">{title}</div><ul>{children}</ul></div>
);

export const SalesTab = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [pm, setPm] = useState("");
  const [sale, setSale] = useState(null);
  const [settings, setSettings] = useState({});
  const [invoice, setInvoice] = useState(null);
  const load = () => api.get("/pos/sales", { params: { q: q || undefined, payment_method: pm || undefined } }).then((r) => setRows(r.data.items));
  const exportCsv = async () => {
    try {
      const r = await api.get("/pos/sales/export", { params: { q: q || undefined, payment_method: pm || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "dealswagon_sales.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export sales"); }
  };
  const exportExcel = async () => {
    try {
      const r = await api.get("/pos/sales/export.xlsx", { params: { q: q || undefined, payment_method: pm || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "dealswagon-sales.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Sales Excel downloaded");
    } catch { toast.error("Could not export sales"); }
  };
  useEffect(() => { api.get("/pos/settings").then((r) => setSettings(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, pm]);
  const open = (id) => api.get(`/pos/sales/${id}`).then((r) => setSale(r.data));
  return (
    <div className="space-y-4" data-testid="sales-tab">
      <div className="flex gap-2 flex-wrap items-center">
        <div className="font-heading text-2xl text-brand-navy flex-1">Sales History</div>
        <button onClick={exportCsv} data-testid="sales-export-btn" className="h-10 px-3 rounded-lg border border-brand-navy text-brand-navy font-bold text-sm inline-flex items-center gap-1"><Download className="h-4 w-4" /> Export CSV</button>
        <button onClick={exportExcel} data-testid="sales-export-excel-btn" className="h-10 px-3 rounded-lg bg-brand-navy text-white font-bold text-sm inline-flex items-center gap-1"><FileSpreadsheet className="h-4 w-4" /> Download Excel</button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice, product, cashier…" data-testid="sales-search" className="h-10 border rounded-lg px-3 flex-1 min-w-[200px]" />
        <select value={pm} onChange={(e) => setPm(e.target.value)} className="h-10 border rounded-lg px-2"><option value="">All payments</option>{PAY.map((m) => <option key={m}>{m}</option>)}</select>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="sales-table">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Invoice</th><th className="p-3">Date</th><th className="p-3">Cashier</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">No sales yet.</td></tr> :
              rows.map((s) => (
                <tr key={s.sale_id} onClick={() => open(s.sale_id)} data-testid={`sale-row-${s.sale_id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="p-3 font-bold">{s.invoice_number}</td>
                  <td className="p-3">{new Date(s.created_at).toLocaleString("en-CA")}</td>
                  <td className="p-3">{s.cashier_name}</td>
                  <td className="p-3">{money(s.total)}</td>
                  <td className="p-3">{s.payment_method}</td>
                  <td className="p-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.status === "COMPLETED" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {sale && <SaleDetail sale={sale} settings={settings} onClose={() => setSale(null)} onChanged={() => { open(sale.sale_id); load(); }} onReprint={setInvoice} />}
      {invoice && <InvoiceModal sale={invoice} settings={settings} onClose={() => setInvoice(null)} showClose />}
    </div>
  );
};

const SaleDetail = ({ sale, settings, onClose, onChanged, onReprint }) => {
  const [returning, setReturning] = useState(false);
  const [qty, setQty] = useState({});
  const [method, setMethod] = useState(sale.payment_method);
  const [returnDoc, setReturnDoc] = useState(null);
  const canReturn = true;
  const lk = (l) => l.line_id || l.product_id;
  const returnedByLine = {};
  (sale.returns || []).forEach((r) => r.items.forEach((i) => { const k = i.line_id || i.product_id; returnedByLine[k] = (returnedByLine[k] || 0) + i.quantity; }));
  const submitReturn = async () => {
    const items = Object.entries(qty).filter(([, q]) => q > 0).map(([line_id, quantity]) => ({ line_id, quantity }));
    if (!items.length) { toast.error("Select quantities to return"); return; }
    try {
      const r = await api.post(`/pos/sales/${sale.sale_id}/return`, { items, refund_method: method });
      toast.success(`Refunded ${money(r.data.refund_amount)}`);
      setReturning(false); setQty({}); setReturnDoc(r.data); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Return failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sale-detail-modal">
        <div className="flex justify-between items-center"><div className="font-heading text-xl text-brand-navy">{sale.invoice_number}</div><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="text-sm text-slate-500">{new Date(sale.created_at).toLocaleString("en-CA")} · {sale.cashier_name} · {sale.payment_method}</div>
        <table className="w-full text-sm mt-3">
          <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-1">Item</th><th>Qty</th><th className="text-right">Amount</th>{returning && <th className="text-right">Return</th>}</tr></thead>
          <tbody>
            {sale.items.map((l) => {
              const k = lk(l);
              const remaining = l.quantity - (returnedByLine[k] || 0);
              return (
                <tr key={k} className="border-t">
                  <td className="py-1">{l.product_name}<div className="text-xs text-slate-400">{l.sku}</div></td>
                  <td>{l.quantity}</td>
                  <td className="text-right">{money(l.line_total)}</td>
                  {returning && <td className="text-right"><input type="number" min="0" max={remaining} value={qty[k] || 0} onChange={(e) => setQty({ ...qty, [k]: Math.max(0, Math.min(remaining, parseInt(e.target.value) || 0)) })} data-testid={`return-qty-${k}`} className="w-16 h-8 border rounded text-right px-1" /> <span className="text-xs text-slate-400">/{remaining}</span></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="mt-3 space-y-1 text-sm border-t pt-2">
          <div className="flex justify-between"><span>Subtotal</span><span>{money(sale.subtotal)}</span></div>
          <div className="flex justify-between"><span>HST ({sale.hst_rate}%)</span><span>{money(sale.hst_amount)}</span></div>
          <div className="flex justify-between font-bold text-brand-navy"><span>Total</span><span>{money(sale.total)}</span></div>
          {sale.refunded_amount > 0 && <div className="flex justify-between text-brand-red"><span>Refunded</span><span>-{money(sale.refunded_amount)}</span></div>}
        </div>
        {returning ? (
          <div className="mt-4 space-y-2">
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-10 border rounded-lg px-2">{PAY.map((m) => <option key={m}>{m}</option>)}</select>
            <div className="flex gap-2">
              <button onClick={submitReturn} data-testid="confirm-return" className="flex-1 h-11 rounded-lg bg-brand-red text-white font-bold">Process refund</button>
              <button onClick={() => setReturning(false)} className="h-11 px-4 rounded-lg border">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 mt-4">
            <button onClick={() => onReprint(sale)} data-testid="sale-reprint" className="h-11 rounded-lg bg-brand-navy text-white font-bold inline-flex items-center justify-center gap-1"><Printer className="h-4 w-4" /> Print</button>
            <button onClick={() => downloadReceiptPDF(sale, settings)} className="h-11 rounded-lg border border-brand-navy text-brand-navy font-bold inline-flex items-center justify-center gap-1"><Download className="h-4 w-4" /> PDF</button>
            {canReturn && sale.status !== "REFUNDED" && sale.source !== "ORDER" && <button onClick={() => setReturning(true)} data-testid="start-return" className="h-11 rounded-lg bg-amber-500 text-white font-bold inline-flex items-center justify-center gap-1"><RotateCcw className="h-4 w-4" /> Return</button>}
          </div>
        )}
        {!returning && sale.source === "ORDER" && <p className="text-xs text-slate-400 mt-2 text-center" data-testid="sale-order-note">Paid online order — refunds are handled in the Orders tab.</p>}
      </div>
      {returnDoc && <ReturnReceiptModal ret={returnDoc} settings={settings} onClose={() => setReturnDoc(null)} />}
    </div>
  );
};

const ReturnReceiptModal = ({ ret, settings, onClose }) => (
  <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center" onClick={(e) => e.stopPropagation()} data-testid="return-receipt-modal">
      <CheckCircle2 className="h-12 w-12 text-brand-red mx-auto" />
      <h2 className="font-heading text-2xl text-brand-navy mt-2">Refund Processed</h2>
      <div className="text-slate-500">Original invoice <b className="text-brand-navy">{ret.invoice_number}</b></div>
      <div className="inline-block mt-3 border-2 border-brand-red rounded-xl px-6 py-2">
        <div className="text-xs uppercase tracking-widest text-brand-red font-bold">Total refund</div>
        <div className="font-heading text-3xl text-brand-red" data-testid="return-refund-amount">-{money(ret.refund_amount)}</div>
      </div>
      <div className="text-xs text-slate-400 mt-2">Refunded to {ret.refund_method} · {new Date(ret.created_at || Date.now()).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}</div>
      <div className="grid grid-cols-2 gap-2 mt-5">
        <button onClick={() => printReturn(ret, settings)} data-testid="print-return-receipt" className="h-12 rounded-lg bg-brand-red text-white font-bold inline-flex items-center justify-center gap-2"><Printer className="h-4 w-4" /> Print refund</button>
        <button onClick={() => downloadReturnPDF(ret, settings)} data-testid="download-return-receipt" className="h-12 rounded-lg border border-brand-red text-brand-red font-bold inline-flex items-center justify-center gap-2"><Download className="h-4 w-4" /> PDF</button>
      </div>
      <button onClick={onClose} data-testid="return-receipt-done" className="w-full h-12 mt-3 rounded-lg bg-brand-navy text-white font-bold">Done</button>
    </div>
  </div>
);

export const StaffTab = () => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "CASHIER", can_return: false });
  const load = () => api.get("/pos/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Name, email, and 6+ char password required"); return; }
    try { await api.post("/pos/users", form); toast.success("Staff created"); setForm({ name: "", email: "", password: "", role: "CASHIER", can_return: false }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const update = async (u, patch) => { try { await api.put(`/pos/users/${u.user_id}`, patch); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } };
  const deactivate = async (u) => { try { await api.delete(`/pos/users/${u.user_id}`); toast.success("Deactivated"); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-4" data-testid="staff-tab">
      <div className="font-heading text-2xl text-brand-navy">Prepare Order Staff & Users</div>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="font-bold text-brand-navy mb-2">Add staff account</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" className="h-10 border rounded-lg px-3" />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" className="h-10 border rounded-lg px-3" />
          <input placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" className="h-10 border rounded-lg px-3" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role" className="h-10 border rounded-lg px-2"><option value="CASHIER">Cashier</option><option value="ADMIN">Admin</option></select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.can_return} onChange={(e) => setForm({ ...form, can_return: e.target.checked })} data-testid="staff-can-return" /> Can process returns</label>
        </div>
        <button onClick={create} data-testid="staff-create-btn" className="mt-3 h-11 px-6 rounded-full bg-brand-navy text-white font-bold inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Create</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Returns</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-slate-100" data-testid={`staff-row-${u.user_id}`}>
                <td className="p-3 font-bold">{u.name}</td><td className="p-3">{u.email}</td>
                <td className="p-3">{u.role}</td>
                <td className="p-3">{u.role === "CASHIER" ? <input type="checkbox" checked={u.can_return} onChange={(e) => update(u, { can_return: e.target.checked })} data-testid={`staff-toggle-return-${u.user_id}`} /> : "—"}</td>
                <td className="p-3">{u.is_active ? <span className="text-green-700 font-bold">Active</span> : <span className="text-slate-400">Inactive</span>}</td>
                <td className="p-3 text-right">{u.is_active && u.role !== "SUPER_ADMIN" && <button onClick={() => deactivate(u)} data-testid={`staff-deactivate-${u.user_id}`} className="text-brand-red font-bold inline-flex items-center gap-1"><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const POSSettingsTab = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/pos/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-400">Loading…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => { try { const r = await api.put("/pos/settings", s); setS(r.data); toast.success("Prepare Order settings saved"); } catch { toast.error("Save failed"); } };
  const F = ({ label, k, type = "text" }) => (
    <label className="block"><span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
      <input type={type} value={s[k] ?? ""} onChange={(e) => set(k, type === "number" ? parseFloat(e.target.value) : e.target.value)} data-testid={`pos-setting-${k}`} className="mt-1 w-full h-10 border rounded-lg px-3" /></label>
  );
  return (
    <div className="space-y-4" data-testid="pos-settings-tab">
      <div className="font-heading text-2xl text-brand-navy">Prepare Order Settings</div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 grid sm:grid-cols-2 gap-3">
        <F label="Store name" k="store_name" />
        <F label="Currency" k="currency" />
        <label className="block sm:col-span-2"><span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Store address / location (one line per line on the order summary)</span>
          <textarea rows={4} value={s.store_address_block ?? ""} onChange={(e) => set("store_address_block", e.target.value)} data-testid="pos-setting-store_address_block" className="mt-1 w-full border rounded-lg p-2" /></label>
        <F label="HST rate (%)" k="hst_rate" type="number" />
        <F label="Low-stock threshold" k="low_stock_threshold" type="number" />
        <F label="Invoice prefix" k="invoice_prefix" />
        <F label="Invoice starting number" k="invoice_start" type="number" />
        <label className="block sm:col-span-2"><span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Invoice footer</span>
          <textarea rows={2} value={s.invoice_footer} onChange={(e) => set("invoice_footer", e.target.value)} data-testid="pos-setting-invoice_footer" className="mt-1 w-full border rounded-lg p-2" /></label>
        <label className="block sm:col-span-2"><span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Interac e-Transfer details (shown to customers)</span>
          <textarea rows={3} value={s.store_etransfer ?? ""} onChange={(e) => set("store_etransfer", e.target.value)} data-testid="pos-setting-store_etransfer" className="mt-1 w-full border rounded-lg p-2" placeholder={"Send Interac e-Transfer to pay@dealswagon.ca (auto-deposit).\nUse your order number as the message."} /></label>
        <label className="block sm:col-span-2"><span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Pay-link message (email + pay page · blank = default)</span>
          <textarea rows={2} value={s.paylink_message ?? ""} onChange={(e) => set("paylink_message", e.target.value)} data-testid="pos-setting-paylink_message" className="mt-1 w-full border rounded-lg p-2" placeholder="Thanks for shopping with DealsWagon! Tap below to review your items and pay securely." /></label>
        <label className="block sm:col-span-2"><span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Terms &amp; Conditions</span>
          <textarea rows={3} value={s.return_policy} onChange={(e) => set("return_policy", e.target.value)} data-testid="pos-setting-return_policy" className="mt-1 w-full border rounded-lg p-2" /></label>
      </div>
      <button onClick={save} data-testid="pos-settings-save" className="h-11 px-6 rounded-full bg-brand-navy text-white font-bold">Save POS settings</button>
      <p className="text-xs text-slate-400">Note: invoice starting number only applies before the first sale is recorded.</p>
    </div>
  );
};

const RESET_OPTIONS = [
  ["products", "Products + inventory", "All product records and their stock + stock-history."],
  ["sales", "Sales + returns/refunds", "Every invoice, return and refund record. Resets invoice numbering to the start."],
  ["both", "Everything above", "Products, inventory, sales, returns — and resets invoice numbering to DW-000001."],
];

export const DangerZoneTab = () => {
  const [scope, setScope] = useState("sales");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const armed = confirm === "RESET";

  const run = async () => {
    if (!armed) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/pos/admin/reset", { scope, confirm });
      setResult(r.data);
      setConfirm("");
      toast.success("Store data reset");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="danger-zone-tab">
      <div className="font-heading text-2xl text-brand-red">Danger Zone</div>
      <div className="bg-white border-2 border-brand-red/40 rounded-xl p-5 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-brand-red shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-brand-navy">Reset store data</div>
            <p className="text-sm text-slate-600">Permanently clear test data so you can go live with a clean slate. <b>This cannot be undone.</b></p>
            <p className="text-xs text-slate-500 mt-1"><b>Always kept:</b> your admin login, Prepare Order settings (HST, prefix, currency), order summary branding (logo, website, address, footer, policy), and the Merchandise/Clothing Types.</p>
          </div>
        </div>

        <div className="space-y-2">
          {RESET_OPTIONS.map(([val, label, desc]) => (
            <label key={val} data-testid={`reset-opt-${val}`} className={`flex gap-3 p-3 rounded-lg border cursor-pointer ${scope === val ? "border-brand-red bg-red-50" : "border-slate-200"}`}>
              <input type="radio" name="reset-scope" checked={scope === val} onChange={() => setScope(val)} className="mt-1" />
              <span><span className="font-bold text-brand-navy block">{label}</span><span className="text-xs text-slate-600">{desc}</span></span>
            </label>
          ))}
        </div>

        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Type <span className="text-brand-red">RESET</span> to confirm</label>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" data-testid="reset-confirm-input"
            className="mt-1 w-full h-11 border border-slate-300 rounded-lg px-3 font-mono tracking-widest outline-none focus:border-brand-red" />
        </div>

        <button onClick={run} disabled={!armed || busy} data-testid="reset-run-btn"
          className={`h-12 px-6 rounded-lg font-bold w-full sm:w-auto ${armed && !busy ? "bg-brand-red text-white hover:bg-brand-redHover" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
          {busy ? "Clearing…" : "Clear selected data"}
        </button>

        {result && (
          <div data-testid="reset-result" className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
            Removed <b>{result.products}</b> products, <b>{result.inventory}</b> inventory records, <b>{result.sales}</b> sales and <b>{result.returns}</b> returns{result.invoice_reset ? "; invoice numbering reset to the start" : ""}.
          </div>
        )}
      </div>
    </div>
  );
};
